import type {Thread, Comment} from '@github-ui/conversations'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {pullRequestMarkersKey, type Markers} from '../page-data/loaders/use-markers-data'
import {pullRequestTableOfContentsKey, type TableOfContents} from '../page-data/loaders/use-table-of-contents-data'
import {threadPreviewsQueryKey} from '../page-data/payloads/thread-previews'

type MutationData = {
  text: string
  line?: number
  path: string
  side: string
  startLine?: number
  startSide: string | undefined
  subjectType?: string
  submitBatch?: boolean
  comparisonStartOid?: string | null | undefined // implement later to comment on commit range
  comparisonEndOid?: string | null | undefined // implement later to comment on commit range
}

type MutationResponse = {
  thread: Thread
  comment: Comment
  totalCommentsCount: number
}

export function useCreateReviewCommentMutation(pullRequestPathName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `${pullRequestPathName}/page_data/${PageData.createReviewComment}`

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: data,
      })

      const json = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, json)
      return json
    },
    onSuccess: (data: MutationResponse, inputs: MutationData) => {
      // update the MarkersMap for the TOC store
      const diffLineKey = inputs.side === 'RIGHT' ? `R${inputs.line}` : `L${inputs.line}`
      const startPosition =
        inputs.startSide && inputs.startLine
          ? inputs.startSide === 'right'
            ? `R${inputs.startLine}`
            : `L${inputs.startLine}`
          : undefined

      // update the Markers Store
      queryClient.setQueryData(pullRequestMarkersKey(pullRequestPathName), (oldMarkersData: Markers) => {
        return {
          ...oldMarkersData,
          threads: {
            ...oldMarkersData?.threads,
            [data.thread.id]: {
              ...data.thread,
            },
          },
        }
      })

      queryClient.setQueryData(
        pullRequestTableOfContentsKey(pullRequestPathName),
        (oldTableofContentsData: TableOfContents) => {
          return oldTableofContentsData?.map(diff => {
            if (diff.path !== inputs.path) {
              return diff
            }
            const markersMap = diff.markersMap ?? {}
            return {
              ...diff,
              totalCommentsCount: data.totalCommentsCount,
              markersMap: {
                ...diff.markersMap,
                [diffLineKey]: {
                  ...markersMap[diffLineKey],
                  threads: (markersMap[diffLineKey]?.threads ?? []).concat({
                    id: parseInt(data.thread.id),
                    start: startPosition,
                  }),
                },
              },
            }
          })
        },
      )
      // invalidate pullRequestFilesToolbar queries
      queryClient.invalidateQueries({
        queryKey: threadPreviewsQueryKey(pullRequestPathName),
      })
    },
  })
}
